import React, { useMemo } from 'react';
import { Typography, Card, Row, Col, Statistic, Spin, Alert, Space, Divider } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  FormOutlined,
  PlusOutlined,
  OrderedListOutlined,
  ExperimentOutlined,
  BarChartOutlined,
  DashboardOutlined,
  AuditOutlined,
  FileDoneOutlined,
  ToolOutlined,
  FileSearchOutlined
} from '@ant-design/icons';
import type { VwQaqcInspRecords, QaqcRfiForms, QaqcPipeInspectionRecord } from '../../types';
import './QaqcDashboard.css';
import Button from '../../components/common/Button';

const { Title } = Typography;

const QaqcDashboard: React.FC = () => {
  const navigate = useNavigate();

  // Fetch inspection records
  const { data: inspectionRecords = [], isLoading: loadingInspections, error: errorInspections } = useQuery<VwQaqcInspRecords[], Error>({
    queryKey: ['vw_qaqc_insp_records'],
    queryFn: () => fetchSqlData('vw_qaqc_insp_records'),
    staleTime: 1000 * 60 * 5,
  });

  // Fetch RFI forms
  const { data: rfiForms = [], isLoading: loadingRfis, error: errorRfis } = useQuery<QaqcRfiForms[], Error>({
    queryKey: ['qaqc_rfi_forms'],
    queryFn: () => fetchSqlData('qaqc_rfi_forms'),
    staleTime: 1000 * 60 * 5,
  });

  // Fetch pipe inspection records
  const { data: pipeInspections = [], isLoading: loadingPipeInsp, error: errorPipeInsp } = useQuery<QaqcPipeInspectionRecord[], Error>({
    queryKey: ['qaqc_pipe_inspection_records'],
    queryFn: () => fetchSqlData('qaqc_pipe_inspection_records'),
    staleTime: 1000 * 60 * 5,
  });

  // Calculate statistics
  const totalInspections = inspectionRecords.length;
  const passedInspections = useMemo(() => inspectionRecords.filter((rec) => rec.form_result === true).length, [inspectionRecords]);
  const failedInspections = useMemo(() => inspectionRecords.filter((rec) => rec.form_result === false).length, [inspectionRecords]);

  const totalRfis = rfiForms.length;
  const activeRfis = useMemo(() => rfiForms.filter((rfi) => rfi.rfi_status === 'active').length, [rfiForms]);
  const draftRfis = useMemo(() => rfiForms.filter((rfi) => rfi.rfi_status === 'draft').length, [rfiForms]);

  const totalPipeInspections = pipeInspections.length;

  // Calculate percentages
  const passRate = totalInspections > 0 ? Math.round((passedInspections / totalInspections) * 100) : 0;
  const failRate = totalInspections > 0 ? Math.round((failedInspections / totalInspections) * 100) : 0;
  const activeRfiRate = totalRfis > 0 ? Math.round((activeRfis / totalRfis) * 100) : 0;

  if (loadingInspections || loadingRfis || loadingPipeInsp) {
    return <Spin tip="Loading Dashboard..." style={{ width: '100%', marginTop: 48 }} />;
  }

  if (errorInspections || errorRfis || errorPipeInsp) {
    return (
      <Alert
        type="error"
        message="Failed to Load Dashboard Data"
        description={String(errorInspections?.message || errorRfis?.message || errorPipeInsp?.message)}
        showIcon
      />
    );
  }

  return (
    <div className="qaqc-dashboard">
      {/* Header and Quick Actions */}
      <div className="dashboard-header">
        <Title level={2} className="dashboard-title">
          <DashboardOutlined style={{ marginRight: 12, color: '#1890ff' }} />
          QA/QC Dashboard
        </Title>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card className="quick-actions-card">
              <Space wrap size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                <Button
                  action="add"
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={() => navigate('/qaqc/inspections/records/create')}
                >
                  New Inspection
                </Button>
                <Button
                  icon={<OrderedListOutlined />}
                  size="large"
                  onClick={() => navigate('/qaqc/inspections/records')}
                >
                  Inspection List
                </Button>
                <Button
                  action="add"
                  icon={<FileTextOutlined />}
                  size="large"
                  onClick={() => navigate('/qaqc/rfi/create')}
                >
                  New RFI
                </Button>
                <Button
                  icon={<FileDoneOutlined />}
                  size="large"
                  onClick={() => navigate('/qaqc/rfi/list')}
                >
                  RFI List
                </Button>
                <Button
                  icon={<BarChartOutlined />}
                  size="large"
                  onClick={() => navigate('/qaqc/ndt/reports/bc')}
                >
                  Reports
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={24} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            className="stats-card palette-blue"
            onClick={() => navigate('/qaqc/inspections/records')}
          >
            <Statistic
              title={
                <span className="stats-title">
                  <AuditOutlined style={{ marginRight: 8 }} />
                  Total Inspections
                </span>
              }
              value={totalInspections}
              valueStyle={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 'bold' }}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            className="stats-card palette-green"
          >
            <Statistic
              title={
                <span className="stats-title">
                  <CheckCircleOutlined style={{ marginRight: 8 }} />
                  Passed
                </span>
              }
              value={passedInspections}
              valueStyle={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 'bold' }}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            className="stats-card palette-red"
          >
            <Statistic
              title={
                <span className="stats-title">
                  <CloseCircleOutlined style={{ marginRight: 8 }} />
                  Failed
                </span>
              }
              value={failedInspections}
              valueStyle={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 'bold' }}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            className="stats-card palette-orange"
            onClick={() => navigate('/qaqc/rfi/list')}
          >
            <Statistic
              title={
                <span className="stats-title">
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  Total RFIs
                </span>
              }
              value={totalRfis}
              valueStyle={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 'bold' }}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Access Menu and Quick Statistics */}
      <Row gutter={24} style={{ marginBottom: 32 }}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <span className="quick-menu-title">
                <FormOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                Quick Access Menu
              </span>
            }
            className="quick-menu-card"
          >
            <div className="quick-tiles" role="list">
              <button className="quick-tile variant-blue" onClick={() => navigate('/qaqc/inspections/records')} role="listitem">
                <OrderedListOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">Inspection Records</span>
              </button>
              <button className="quick-tile variant-green" onClick={() => navigate('/qaqc/inspections/records/create')} role="listitem">
                <PlusOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">New Inspection</span>
              </button>
              <button className="quick-tile variant-orange" onClick={() => navigate('/qaqc/inspections/form_list')} role="listitem">
                <FormOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">Inspection Forms</span>
              </button>
              <button className="quick-tile variant-purple" onClick={() => navigate('/qaqc/rfi/list')} role="listitem">
                <FileTextOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">RFI Forms</span>
              </button>
              <button className="quick-tile variant-cyan" onClick={() => navigate('/qaqc/ndt/pipe_inspections/inspection_list')} role="listitem">
                <ExperimentOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">Piping Inspections</span>
              </button>
              <button className="quick-tile variant-blue" onClick={() => navigate('/qaqc/ndt/reports/bc')} role="listitem">
                <BarChartOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">NDT Reports</span>
              </button>
              <button className="quick-tile variant-green" onClick={() => navigate('/qaqc/piping/operations/weldingdata')} role="listitem">
                <ToolOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">Welding Data</span>
              </button>
              <button className="quick-tile variant-red" onClick={() => navigate('/qaqc/piping/tools/welders')} role="listitem">
                <FileSearchOutlined className="quick-tile__icon" />
                <span className="quick-tile__label">Admin Tools</span>
              </button>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title={
              <span className="quick-menu-title">
                <BarChartOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                Quick Statistics
              </span>
            }
            className="quick-menu-card"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Pass Rate:</span>
                <strong style={{ color: '#52c41a', fontSize: '18px' }}>
                  {passRate}%
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Fail Rate:</span>
                <strong style={{ color: '#ff4d4f', fontSize: '18px' }}>
                  {failRate}%
                </strong>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Active RFIs:</span>
                <strong style={{ color: '#1890ff', fontSize: '18px' }}>
                  {activeRfis}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Draft RFIs:</span>
                <strong style={{ color: '#faad14', fontSize: '18px' }}>
                  {draftRfis}
                </strong>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Pipe Inspections:</span>
                <strong style={{ color: '#722ed1', fontSize: '18px' }}>
                  {totalPipeInspections}
                </strong>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Module Breakdown */}
      <Row gutter={24} style={{ marginBottom: 32 }}>
        <Col span={24}>
          <Card
            title={
              <span className="quick-menu-title">
                <FileDoneOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                Module Overview
              </span>
            }
            className="quick-menu-card"
            headStyle={{ textAlign: 'center' }}
          >
            <Row gutter={16} justify="center">
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  background: '#1890ff',
                  borderRadius: '12px',
                  color: '#ffffff',
                  marginBottom: '16px',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/qaqc/inspections/records')}
                >
                  <AuditOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {totalInspections}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    Total Inspections
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  background: '#fa8c16',
                  borderRadius: '12px',
                  color: '#ffffff',
                  marginBottom: '16px',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/qaqc/rfi/list')}
                >
                  <FileTextOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {totalRfis}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    RFI Forms
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  background: '#722ed1',
                  borderRadius: '12px',
                  color: '#ffffff',
                  marginBottom: '16px',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/qaqc/ndt/pipe_inspections/inspection_list')}
                >
                  <ExperimentOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {totalPipeInspections}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    Piping Inspections
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  background: activeRfiRate > 50 ? '#ff4d4f' : '#52c41a',
                  borderRadius: '12px',
                  color: '#ffffff',
                  marginBottom: '16px'
                }}>
                  <BarChartOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {activeRfiRate}%
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    Active RFI Rate
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Divider style={{ margin: '32px 0' }} />

      {/* Footer */}
      <div className="dashboard-footer">
        <Typography.Text type="secondary">
          Last updated: {new Date().toLocaleString('en-US')}
        </Typography.Text>
      </div>
    </div>
  );
};

export default QaqcDashboard;
